import { Dialog, DialogActions, DialogContent, DialogTitle } from 'components/Dialog';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { Button } from '@mui/material';
import { closeModal } from 'features/modal/modalSlice';
import { closeUnlessEscape } from 'utils/closeUnlessEscape';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from 'store/hooks';
import React, { useEffect, useId } from 'react';
import { usePolls } from 'features/polls/usePolls';
import { usePollTemplates } from 'features/polls/hooks/usePollTemplates';
import PollForm, { PollFormValues } from 'features/polls/components/PollForm';
import { createPollTemplateRequested } from 'features/polls/actions';

const CreatePollTemplateModal = () => {
  const { t } = useTranslation('modals');
  const dispatch = useAppDispatch();

  const modalId = useId();

  const { isFeatureAvailable } = usePolls();
  const { canManageTemplates } = usePollTemplates();

  const methods = useForm<PollFormValues>({
    mode: 'onChange',
    defaultValues: {
      question: '',
      type: 'single',
      choices: [],
      anonymous: false,
    },
  });

  const {
    handleSubmit,
    formState: { isValid },
  } = methods;

  const modalShouldBeClosed = !isFeatureAvailable || !canManageTemplates;

  useEffect(() => {
    if (modalShouldBeClosed) {
      dispatch(closeModal('createPollTemplate'));
    }
  }, [modalShouldBeClosed, dispatch]);

  const createPollTemplate: SubmitHandler<PollFormValues> = (data) => {
    dispatch(
      createPollTemplateRequested({
        question: data.question,
        type: data.type,
        choices: data.type === 'free' ? [] : data.choices.map((choice) => choice.text),
        anonymous: data.anonymous,
      })
    );
    dispatch(closeModal('createPollTemplate'));
  };

  const handleCancel = () => {
    dispatch(closeModal('createPollTemplate'));
  };

  return (
    <Dialog
      open={!modalShouldBeClosed}
      maxWidth="xs"
      aria-labelledby={`${modalId}-title`}
      onClose={closeUnlessEscape(handleCancel)}
      disableRestoreFocus
    >
      <DialogTitle id={`${modalId}-title`}>{t('polls.create_poll_template.title')}</DialogTitle>
      <DialogContent>
        <FormProvider {...methods}>
          <PollForm />
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <Button type="button" color="grayscale" onClick={handleCancel}>
          {t('polls.create_poll_template.btn_cancel_label')}
        </Button>
        <Button type="button" onClick={handleSubmit(createPollTemplate)} disabled={!isValid}>
          {t('polls.create_poll_template.btn_ok_label')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePollTemplateModal;
